import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import { useMutationState } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyGetFieldsV2WebApiHandler_getGeneratedFields } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  AI_DRAFT_WAIT_WINDOW_MS,
  cacheAiDraftFields,
  getCachedAiDraft,
  markAiDraftStarted,
} from '../../utils/aiDraftCacheUtils';

import { GET_PROPERTY_FIELDS_MUTATION_KEY } from './useGetPropertyFields';

import type {
  ListingAlias,
  UserPropertyGetFieldsResponseV2WebDto,
  UserPropertyGetFieldsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { AiDraftFields } from '../../utils/aiDraftCacheUtils';

/** The two endpoints return the same payload under two generated names; map both onto one type. */
type AiFieldsResponse = UserPropertyGetFieldsResponseV2WebDto | UserPropertyGetFieldsResponseWebDto;

const toAiDraftFields = (response?: AiFieldsResponse): AiDraftFields | undefined =>
  response == null
    ? undefined
    : {
        description: { confidence: response.description.confidence, value: response.description.value },
        amenityFields: response.amenityFields.map((amenityField) => ({
          confidence: amenityField.confidence,
          field: amenityField.field,
          value: amenityField.value,
        })),
      };

/** The AI returns a field per amenity it considered, so only the selected ones count as drafted. */
const hasDraftedAmenities = (fields?: AiDraftFields) =>
  Boolean(fields?.amenityFields?.some((amenityField) => amenityField.value));

const hasDraftedDescription = (fields?: AiDraftFields) => Boolean(fields?.description?.value?.trim());

const isUsable = (fields?: AiDraftFields) => hasDraftedDescription(fields) || hasDraftedAmenities(fields);

export type AiGeneratedFieldsStatus = 'none' | 'waiting' | 'failed' | 'ready';

export type AiGeneratedFields = {
  status: AiGeneratedFieldsStatus;
  propertyFields?: AiDraftFields;
  hasAIDescription: boolean;
  hasAIAmenities: boolean;
  hasAIFields: boolean;
};

/**
 * State of the AI draft for the current listing: `waiting` while it generates, `ready` once it
 * returns something usable, `failed` if it errored, `none` when it never ran or came back empty.
 *
 * The live mutation is the source of truth within a session. It is memory only, so the draft is
 * also mirrored into sessionStorage and read back after a reload; if the reload happened mid
 * generation the cache holds only a start marker, and getGeneratedFields fetches the result the
 * server finished while we were away.
 */
export const useAiGeneratedFields = (): AiGeneratedFields => {
  const router = useRouter();
  const listingAlias = router.isReady ? (router.query.listingAlias as string) : '';

  const mutations = useMutationState({
    filters: { mutationKey: GET_PROPERTY_FIELDS_MUTATION_KEY },
    select: (mutation) => ({
      status: mutation.state.status,
      submittedAt: mutation.state.submittedAt,
      data: toAiDraftFields(mutation.state.data as UserPropertyGetFieldsResponseV2WebDto | undefined),
    }),
  });

  const latest = mutations.reduce<(typeof mutations)[number] | undefined>(
    (newest, mutation) => (newest == null || mutation.submittedAt >= newest.submittedAt ? mutation : newest),
    undefined,
  );

  const isGenerating = mutations.some((mutation) => mutation.status === 'pending');
  const liveFields = latest?.status === 'success' && isUsable(latest.data) ? latest.data : undefined;

  // Read during render, keyed on the listing, so a restored draft is on screen the moment the
  // router resolves rather than after an extra paint.
  const cachedDraft = useMemo(() => getCachedAiDraft(listingAlias), [listingAlias]);
  const isDraftUnfinished = cachedDraft != null && cachedDraft.fields == null;
  const isRecentlyStarted = isDraftUnfinished && Date.now() - cachedDraft.startedAtMs < AI_DRAFT_WAIT_WINDOW_MS;

  // Only worth asking the server when this session never ran the draft itself and the cache says
  // one was started but never came back — otherwise the cache or the mutation already has it.
  // Deliberately not age-gated: a draft the server finished should come back however late we ask.
  const { data: generatedFields, isError: didGeneratedFieldsFail } =
    useRentalManagerUserPropertyGetFieldsV2WebApiHandler_getGeneratedFields(
      { listingAlias: listingAlias as ListingAlias },
      {
        enabled: latest == null && isDraftUnfinished && Boolean(listingAlias),
        meta: {
          tags: ['aiGeneratedFields'],
          context: {
            api: 'rental-manager',
            details: 'useRentalManagerUserPropertyGetFieldsV2WebApiHandler_getGeneratedFields',
          },
        },
      },
    );

  const fetchedFields = toAiDraftFields(generatedFields);
  const restoredFields = isUsable(fetchedFields)
    ? fetchedFields
    : isUsable(cachedDraft?.fields)
      ? cachedDraft?.fields
      : undefined;

  const propertyFields = liveFields ?? (latest == null ? restoredFields : undefined);

  useEffect(() => {
    if (!listingAlias) return;
    if (isGenerating) markAiDraftStarted(listingAlias, Date.now());
  }, [isGenerating, listingAlias]);

  useEffect(() => {
    if (!listingAlias || !propertyFields) return;
    cacheAiDraftFields(listingAlias, propertyFields, cachedDraft?.startedAtMs ?? Date.now());
  }, [listingAlias, propertyFields, cachedDraft?.startedAtMs]);

  const hasAIDescription = hasDraftedDescription(propertyFields);
  const hasAIAmenities = hasDraftedAmenities(propertyFields);
  const hasAIFields = hasAIDescription || hasAIAmenities;

  const status: AiGeneratedFieldsStatus = isGenerating
    ? 'waiting'
    : hasAIFields
      ? 'ready'
      : latest?.status === 'error' || didGeneratedFieldsFail
        ? 'failed'
        : isRecentlyStarted
          ? 'waiting'
          : 'none';

  return {
    status,
    propertyFields: hasAIFields ? propertyFields : undefined,
    hasAIDescription,
    hasAIAmenities,
    hasAIFields,
  };
};
